import React, { useContext, useEffect, useRef, useState } from 'react';
import styles from './ActivityLogs.module.css';
import AIEduSearch from '../../components/Search/AIEduSearch';
import AIEduTabMenu from '../../components/TabMenu/AIEduTabMenu';
import AIEduSpinner from '../../components/Spinner/AIEduSpinner';
import AIEduPaginator from '../../components/Paginator/AIEduPaginator';
import AIEduToast from '../../components/Toast/AIEduToast';
import AIEduButton from '../../components/Button/AIEduButton';
import UserSession from './UserSession';
import CircleToSearch from './CircleToSearch';
import LiveTranscript from './LiveTranscript';
import AISummary from './AISummary';
import { useIntl } from 'react-intl';
import { API } from '../../services';
import { useAuthenticate } from '../../hooks';
import { Context } from '../../context/Context';
import { ASC, DESC } from '../../utils/Constants';
import ExportActivityLogs from './ExportActivityLogs';
import {
    setToastComponent,
    showErrorToast,
    showSuccessToast,
} from '../../services/setup/ApiResponseHandler';
import {
    downloadCSVFromTextData,
    formatDateToYYYYMMDD,
} from '../../utils/Utils';
import PageTitle from '../../layout/PageTitle/pageTitle';

const ActivityLogs = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;

    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {};

    const [searchTerm, setSearchTerm] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    const { refreshMap } = useContext(Context);

    const [rows, setRows] = useState(10);
    const [page, setPage] = useState(0);
    const [first, setFirst] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);

    const [activityLogData, setActivityLogData] = useState([]);

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(-1);
    const [visibleExport, setVisibleExport] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [maxEndDate, setMaxEndDate] = useState(new Date());
    const toastRef = useRef(null);

    const [tableKey, setTableKey] = useState({
        userSession: false,
        c2s: false,
        liveTranscript: false,
        aiSummary: false,
    });

    const DEFAULT_SORT_COLUMN = [
        'loginTime',
        'createdTime',
        'createdTime',
        'createdTime',
    ];

    const onPageChange = (e) => {
        const page = e.page;
        const size = e.rows;

        setFirst(e.first);
        setRows(size);
        setPage(page);

        switch (activeIndex) {
            case 0:
                setTableKey({
                    ...tableKey,
                    userSession: !tableKey.userSession,
                });
                break;
            case 1:
                setTableKey({ ...tableKey, c2s: !tableKey.c2s });
                break;
            case 2:
                setTableKey({
                    ...tableKey,
                    liveTranscript: !tableKey.liveTranscript,
                });
                break;
            case 3:
                setTableKey({ ...tableKey, aiSummary: !tableKey.aiSummary });
                break;
        }

        getLogsDetails(
            activeIndex,
            page,
            size,
            sortField,
            sortOrder,
            searchTerm
        );
    };

    const getLogsDetails = (
        tabIndex,
        start,
        rowsPerPage,
        sortBy,
        sort,
        search
    ) => {
        const params = {
            start,
            rowsPerPage,
            sortBy: sortBy || DEFAULT_SORT_COLUMN[tabIndex],
            sort: sort === 1 ? ASC : DESC,
            search,
        };

        setLoading(true);
        switch (tabIndex) {
            case 0:
                API.getAllKPILogs(organizationId, placeId, params)
                    .then(({ data }) => {
                        const { data: logs, total, sortBy, sort } = data ?? {};
                        if (logs?.length === 0 && start > 0 && total > 0) {
                            setFirst(0);
                            setPage(0);
                            getLogsDetails(
                                tabIndex,
                                0,
                                rowsPerPage,
                                sortBy,
                                sortOrder,
                                search
                            );
                        } else {
                            const logsData = [];
                            logs?.forEach((log) => {
                                logsData.push({
                                    userName: log.userName,
                                    screenId: log.screenId,
                                    userEmail: log.userEmail,
                                    loginTime: log.loginTime,
                                    logoutTime: log.logoutTime,
                                    duration: log.duration,
                                });
                            });
                            setActivityLogData(logsData);
                            setTotalRecords(total);
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
                break;
            case 1:
                API.getCircleToSearchLogs(organizationId, placeId, params)
                    .then(({ data }) => {
                        const { data: logs, total, sortBy, sort } = data ?? {};
                        if (logs?.length === 0 && start > 0 && total > 0) {
                            setFirst(0);
                            setPage(0);
                            getLogsDetails(
                                tabIndex,
                                0,
                                rowsPerPage,
                                sortBy,
                                sortOrder,
                                search
                            );
                        } else {
                            const logsData = [];
                            logs?.forEach((log) => {
                                logsData.push({
                                    createdTime: log.createdTime,
                                    userName: log.userName,
                                    screenId: log.screenId,
                                    userEmail: log.userEmail,
                                });
                            });
                            setActivityLogData(logsData);
                            setTotalRecords(total);
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
                break;
            case 2:
                API.getLiveTranscriptLogs(organizationId, placeId, params)
                    .then(({ data }) => {
                        const { data: logs, total, sortBy, sort } = data ?? {};
                        if (logs?.length === 0 && start > 0 && total > 0) {
                            setFirst(0);
                            setPage(0);
                            getLogsDetails(
                                tabIndex,
                                0,
                                rowsPerPage,
                                sortBy,
                                sortOrder,
                                search
                            );
                        } else {
                            const logsData = [];
                            logs?.forEach((log) => {
                                logsData.push({
                                    createdTime: log.createdTime,
                                    userName: log.userName,
                                    screenId: log.screenId,
                                    duration: log.duration,
                                    userEmail: log.userEmail,
                                });
                            });
                            setActivityLogData(logsData);
                            setTotalRecords(total);
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
                break;
            case 3:
                API.getAISummaryLogs(organizationId, placeId, params)
                    .then(({ data }) => {
                        const { data: logs, total, sortBy, sort } = data ?? {};
                        if (logs?.length === 0 && start > 0 && total > 0) {
                            setFirst(0);
                            setPage(0);
                            getLogsDetails(
                                tabIndex,
                                0,
                                rowsPerPage,
                                sortBy,
                                sortOrder,
                                search
                            );
                        } else {
                            const logsData = [];
                            logs?.forEach((log) => {
                                logsData.push({
                                    createdTime: log.createdTime,
                                    userName: log.userName,
                                    screenId: log.screenId,
                                    duration: log.duration,
                                    userEmail: log.userEmail,
                                });
                            });
                            setActivityLogData(logsData);
                            setTotalRecords(total);
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
                break;
        }
    };

    const handleExportActivityLogs = () => {
        const params = {
            startDate: formatDateToYYYYMMDD(startDate),
            endDate: formatDateToYYYYMMDD(endDate),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };

        const handleExportSuccess = (data, fileName) => {
            showSuccessToast('Exported Data Successfully');
            setStartDate(null);
            setEndDate(null);
            setMaxEndDate(new Date());
            setVisibleExport(false);
            downloadCSVFromTextData(data, fileName);
        };
        switch (activeIndex) {
            case 0:
                API.exportUserSession(organizationId, placeId, params).then(
                    (res) => {
                        const { data, status } = res ?? {};
                        if (status === 200) {
                            handleExportSuccess(data, 'userSession.csv');
                        } else {
                            const { error } = data ?? {};
                            if (error) {
                                showErrorToast(error);
                            }
                        }
                    }
                );
                break;
            case 1:
                API.exportCircleToSearchLogs(
                    organizationId,
                    placeId,
                    params
                ).then((res) => {
                    const { data, status } = res ?? {};
                    if (status === 200) {
                        handleExportSuccess(data, 'c2s.csv');
                    } else {
                        const { error } = data ?? {};
                        if (error) {
                            showErrorToast(error);
                        }
                    }
                });
                break;
            case 2:
                API.exportLiveTranscriptLogs(
                    organizationId,
                    placeId,
                    params
                ).then((res) => {
                    const { data, status } = res ?? {};
                    if (status === 200) {
                        handleExportSuccess(data, 'liveTranscript.csv');
                    } else {
                        const { error } = data ?? {};
                        if (error) {
                            showErrorToast(error);
                        }
                    }
                });
                break;
            case 3:
                API.exportAISummaryLogs(organizationId, placeId, params).then(
                    (res) => {
                        const { data, status } = res ?? {};
                        if (status === 200) {
                            handleExportSuccess(data, 'aiSummary.csv');
                        } else {
                            const { error } = data ?? {};
                            if (error) {
                                showErrorToast(error);
                            }
                        }
                    }
                );
                break;
        }
    };

    const resetPageParams = () => {
        setFirst(0);
        setPage(0);
        setSortOrder(-1);
        setSearchTerm('');
    };

    useEffect(() => {
        setToastComponent(toastRef.current);
    }, []);

    useEffect(() => {
        if (activeIndex === 0) {
            setSortField(DEFAULT_SORT_COLUMN[0]);
            resetPageParams();
            getLogsDetails(0, 0, rows);
        } else {
            setActiveIndex(0);
        }
    }, [refreshMap.activity]);

    useEffect(() => {
        setSortField(DEFAULT_SORT_COLUMN[activeIndex]);
        resetPageParams();
        getLogsDetails(activeIndex, 0, rows);
    }, [activeIndex]);

    const handleLogsSearch = () => {
        setFirst(0);
        setPage(0);
        getLogsDetails(activeIndex, 0, rows, sortField, sortOrder, searchTerm);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleLogsSearch();
        }
    };

    const onSort = (e) => {
        const { field, order } = { field: e.sortField, order: e.sortOrder };

        getLogsDetails(
            activeIndex,
            page,
            rows,
            field,
            order,
            !!searchTerm ? searchTerm : null
        );

        setSortField(field);
        setSortOrder(order);
    };

    const sortIcon = (options) => {
        if (options.sortOrder === 1) {
            return (
                <i
                    className='pi pi-sort-up-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
        if (options.sortOrder === -1) {
            return (
                <i
                    className='pi pi-sort-down-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
    };

    const items = [
        {
            header: f({ id: 'AI_EDU_USER_SESSION' }),
            data: (
                <UserSession
                    f={f}
                    dataKey={tableKey.userSession}
                    data={activityLogData}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sortIcon={sortIcon}
                />
            ),
        },
        {
            header: f({ id: 'AI_EDU_CIRCLE_TO_SEARCH' }),
            data: (
                <CircleToSearch
                    f={f}
                    dataKey={tableKey.c2s}
                    data={activityLogData}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sortIcon={sortIcon}
                />
            ),
        },
        {
            header: f({ id: 'AI_EDU_LIVE_TRANSCRIPT' }),
            data: (
                <LiveTranscript
                    f={f}
                    dataKey={tableKey.liveTranscript}
                    data={activityLogData}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sortIcon={sortIcon}
                />
            ),
        },
        {
            header: f({ id: 'AI_EDU_AI_SUMMARY' }),
            data: (
                <AISummary
                    f={f}
                    dataKey={tableKey.aiSummary}
                    data={activityLogData}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sortIcon={sortIcon}
                />
            ),
        },
    ];

    const onTabChange = (e) => {
        setActiveIndex(e.index);
    };

    return (
        <>
            {loading && <AIEduSpinner />}
            <AIEduToast ref={toastRef} />
            <ExportActivityLogs
                startDate={startDate}
                endDate={endDate}
                maxEndDate={maxEndDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                setMaxEndDate={setMaxEndDate}
                visibleExport={visibleExport}
                setVisibleExport={setVisibleExport}
                handleExportActivityLogs={handleExportActivityLogs}
            />
            <div className={styles.activity_logs}>
                <PageTitle
                    title={f({ id: 'AI_EDU_ACTIVITY_LOG' })}
                    showCount={false}
                />
                <div className='flex justify-content-between flex-wrap gap-3'>
                    <AIEduSearch
                        value={searchTerm}
                        setValue={setSearchTerm}
                        placeholder='Search User, Device'
                        searchHandler={handleLogsSearch}
                        handleInputKeyDown={handleInputKeyDown}
                    />
                    <AIEduButton
                        label='Export'
                        className='btnPrimary'
                        onClick={() => setVisibleExport(true)}
                        style={{ height: '40px', width: '8rem' }}
                    />
                </div>
                <div className='mt-2'>
                    <AIEduTabMenu
                        items={items}
                        activeIndex={activeIndex}
                        onTabChange={onTabChange}
                    />
                </div>
                {activityLogData?.length > 0 && (
                    <AIEduPaginator
                        rows={rows}
                        first={first}
                        totalRecords={totalRecords}
                        onPageChange={onPageChange}
                    />
                )}
            </div>
        </>
    );
};

export default ActivityLogs;
